import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppUI } from 'src/context/AppUIProvider/useAppUI';
import useAuthContext from 'src/context/AuthProvider/useAuthContext';
import useCartMutations from 'src/features/cart/presentation/hooks/useCartMutations';
import useCartQuery from 'src/features/cart/presentation/hooks/useCartQuery';
import { Cart, LocalStorageKeys, Product, ProductInfo } from 'src/types/common';
import { setLocalStorageItemWithExpiry } from 'src/utils/localStorage';

const useCartActions = () => {
  const queryClient = useQueryClient();
  const { session, user, setSession } = useAuthContext();
  const { alertNotification } = useAppUI();
  const [isMigratingProducts, setIsMigratingProducts] = useState(false);
  const navigate = useNavigate();
  const { addToCart, removeFromCart, createCart, migrateCartProducts } =
    useCartMutations();

  const cartQuery = useCartQuery();

  const cartproducts = cartQuery.data?.products || [];

  const addProductToCart = async ({
    product,
    quantity,
    price,
  }: {
    product: ProductInfo;
    quantity: number;
    price: number;
  }) => {
    if (!user || !session || !session?.accessToken) {
      setSession && setSession(undefined);
      setLocalStorageItemWithExpiry(
        LocalStorageKeys.AnonymousWantedProduct,
        product,
        1.8e6
      );
      alertNotification.info(
        'Inicia sesión para agregar productos a tu carrito'
      );
      navigate('/login');
      return;
    }

    if (!user?.active_cart || !user?.main_address) {
      navigate('/addresslist');
      return;
    }

    return addToCart.mutateAsync({
      productId: product.pk,
      product,
      quantity,
      price,
      cartId: user?.active_cart,
      accessToken: session?.accessToken,
    });
  };

  const removeProductFromCart = async (productId: number) => {
    if (!user || !session || !session?.accessToken || !user?.active_cart) {
      setSession && setSession(undefined);
      alertNotification.error('La sesión caducó. Inicia sesión nuevamente.');
      navigate('/login');
      return;
    }

    removeFromCart.mutate({
      productId,
      accessToken: session?.accessToken as string,
      cartId: user?.active_cart as number,
    });
  };

  const createUserCart = async ({
    storeSlugName,
    addressId,
    removeProducts,
  }: {
    storeSlugName?: string;
    addressId: number;
    removeProducts?: boolean;
  }) => {
    if (!user) return;
    if (storeSlugName) {
      setIsMigratingProducts(true);
      const newCart = await createCart.mutateAsync(
        {
          accessToken: session?.accessToken as string,
          storeSlugName,
          addressId,
        },
        {
          onSuccess: async (data) => {
            const queryKey = [
              'cart',
              { userId: user?.pk, accessToken: session?.accessToken },
            ];

            queryClient.setQueryData<Cart>(queryKey, (oldData): Cart => {
              if (data) {
                return data;
              } else if (oldData) {
                return oldData;
              }
              return {} as Cart;
            });

            const prevCartItems = [...cartproducts];

            if (!removeProducts && prevCartItems.length) {
              await handleMigrateCartProducts(
                prevCartItems,
                data,
                storeSlugName
              );

              await queryClient.invalidateQueries({ queryKey: ['cart'] });
            }
            setIsMigratingProducts(false);
          },
          onError: () => setIsMigratingProducts(false),
        }
      );

      return newCart;
    } else {
      return undefined;
    }
  };

  const handleMigrateCartProducts = (
    prevCartItems: Product[],
    newCart: Cart,
    storeSlugName: string
  ) => {
    if (!session?.accessToken) return;

    return migrateCartProducts.mutateAsync({
      prevCartItems,
      newCart,
      storeSlugName,
      accessToken: session?.accessToken,
    });
  };

  return {
    addProductToCart,
    removeProductFromCart,
    createUserCart,
    isUpdatingCart:
      addToCart.isPending || removeFromCart.isPending || isMigratingProducts,
    isMigratingProducts,
  };
};

export default useCartActions;
